import dayjs from 'dayjs'
import { useCallback, useEffect, useRef, useState } from 'react'

interface QuizCountdownResult {
  remainingSeconds: number | null
  formattedTime: string | null
  isExpired: boolean
}

function formatCountdown(rawSeconds: number): string {
  const seconds = Math.floor(rawSeconds)
  if (seconds >= 86400) {
    return `${Math.floor(seconds / 86400)}일 남음`
  }
  if (seconds >= 3600) {
    return `${Math.floor(seconds / 3600)}시간 남음`
  }
  if (seconds >= 60) {
    return `${Math.floor(seconds / 60)}분 남음`
  }
  return `${seconds}초 남음`
}

function useQuizCountdown(endsDate: string | null | undefined): QuizCountdownResult {
  const calcRemaining = useCallback(() => {
    if (!endsDate) return null
    const diff = dayjs(endsDate).diff(dayjs(), 'second')
    return diff > 0 ? diff : 0
  }, [endsDate])

  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(calcRemaining)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (!endsDate) {
      setRemainingSeconds(null)
      return
    }

    setRemainingSeconds(calcRemaining())

    intervalRef.current = setInterval(() => {
      const diff = dayjs(endsDate).diff(dayjs(), 'second')
      if (diff <= 0) {
        setRemainingSeconds(0)
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
      } else {
        setRemainingSeconds(diff)
      }
    }, 1000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [endsDate, calcRemaining])

  const isExpired = remainingSeconds !== null && remainingSeconds <= 0
  const formattedTime = remainingSeconds !== null && remainingSeconds > 0
    ? formatCountdown(remainingSeconds)
    : null

  return { remainingSeconds, formattedTime, isExpired }
}

export { useQuizCountdown }
